import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

type Props = {
  /** Full image URLs, cover first. */
  images: string[];
  name: string;
  /** Index to open on. */
  startIndex: number;
  onClose: () => void;
};

const navBtnClass =
  "flex h-12 w-12 flex-none items-center justify-center rounded-full border border-white/20 bg-white/10 text-[22px] text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-40";

/**
 * Full-screen photo lightbox for the tour gallery. Self-contained: owns the
 * current index, wraps at both ends, and handles keyboard (Esc / ← / →),
 * scrim-click close, body-scroll lock, focus trap + restore, and a11y roles.
 * Mount/unmount is animated by the parent's <AnimatePresence>.
 */
const Lightbox = ({ images, name, startIndex, onClose }: Props) => {
  const [index, setIndex] = useState(startIndex);
  const [broken, setBroken] = useState(false);
  const n = images.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const go = useCallback(
    (delta: number) => {
      setBroken(false);
      setIndex((i) => (i + delta + n) % n);
    },
    [n]
  );

  const jumpTo = (i: number) => {
    setBroken(false);
    setIndex(i);
  };

  // Keyboard: Esc closes, arrows navigate, Tab is trapped inside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight") {
        go(1);
      } else if (e.key === "ArrowLeft") {
        go(-1);
      } else if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  // Lock body scroll while open; restore on close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Move focus into the dialog on open; return it to the trigger on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={`${name} — photo gallery`}
      ref={dialogRef}
      className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
    >
      {/* Top bar: counter + close */}
      <div
        className="flex items-center justify-between px-5 py-4 md:px-8"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-mono text-[13px] tracking-[0.08em] text-sand/80">
          {index + 1} / {n}
        </span>
        <button
          ref={closeRef}
          onClick={onClose}
          aria-label="Close gallery"
          className={navBtnClass}
        >
          ✕
        </button>
      </div>

      {/* Stage: prev / image / next */}
      <div
        className="flex min-h-0 flex-1 items-center justify-center gap-3 px-3 md:gap-6 md:px-6"
        onClick={(e) => e.stopPropagation()}
      >
        {n > 1 && (
          <button
            onClick={() => go(-1)}
            aria-label="Previous photo"
            className={navBtnClass}
          >
            ‹
          </button>
        )}

        <div className="flex min-w-0 flex-1 items-center justify-center">
          {broken ? (
            <div className="text-center text-[14px] text-sand/70">
              This photo couldn't be loaded.
            </div>
          ) : (
            <img
              key={index}
              src={images[index]}
              alt={`${name} — photo ${index + 1} of ${n}`}
              crossOrigin="anonymous"
              onError={() => setBroken(true)}
              className="max-h-[74vh] max-w-full rounded-[14px] object-contain shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
            />
          )}
        </div>

        {n > 1 && (
          <button
            onClick={() => go(1)}
            aria-label="Next photo"
            className={navBtnClass}
          >
            ›
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {n > 1 && (
        <div
          className="flex justify-center px-4 pb-5 pt-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex max-w-full gap-2 overflow-x-auto py-1">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => jumpTo(i)}
                aria-label={`View photo ${i + 1}`}
                aria-current={i === index}
                className={`h-14 w-20 flex-none overflow-hidden rounded-lg transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ${
                  i === index
                    ? "ring-2 ring-primary-500"
                    : "opacity-55 hover:opacity-100"
                }`}
              >
                <img
                  src={src}
                  alt=""
                  crossOrigin="anonymous"
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Lightbox;
